// Import controller interfaces
import {
    INewBusinessApplicationController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api base controller.
import ApiController from './ApiController';
// Import api models.
import {
    AcknowledgementText,
    AddressLocationAsMailingAddress,
    BindingAuthorityEligibilityResult,
    BuildingRequestCommercialExisting,
    BuildingRequestPersonal,
    DisqualifyingConditionsAgreement,
    DocumentUpload,
    ErrorMessage,
    InsuredInfo,
    MortgageeRequest,
    MortgageeRequestWithStatusAndId,
    NewBusinessApplication,
    NewBusinessApplicationDocument,
    PerilDeductible,
    PossibleDisclosureTexts,
    PossiblePeril,
    Signature,
} from '@app/models/api';

export default class ProductionNewBusinessApplicationController extends ApiController implements INewBusinessApplicationController {

	private readonly controllerUrl: string = 'v2/application';

	public constructor() {
		super();
    }

    public async startNewBusinessApplicationPersonal(propertyAddress: AddressLocationAsMailingAddress,
        perilsDeductibles: PerilDeductible[],
        disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/pers/start-application`;
        let data = {
            'buildingLocation': propertyAddress,
            'perilsDeductibles': perilsDeductibles,
            'disqualifyingConditionsAgreement': disqualifyingConditionsAgreement,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not start application.';
            }
            throw error;
        }
    }

    public async startNewBusinessApplicationCommercial(propertyAddress: AddressLocationAsMailingAddress,
        perilsDeductibles: PerilDeductible[],
        disqualifyingConditionsAgreement: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/comm/start-application`;
        let data = {
            'buildingLocations': [propertyAddress],
            'perilsDeductibles': perilsDeductibles,
            'disqualifyingConditionsAgreement': disqualifyingConditionsAgreement,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not start application.';
            }
            throw error;
        }
    }

    public async rateNewBusinessApplicationPersonal(newBusinessApplicationId: number,
        perilsDeductibles?: PerilDeductible[],
        risk?: BuildingRequestPersonal,
        disqualifyingConditionsAgreement?: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        let url: string = `${this.controllerUrl}/pers/price-indication`;
        let data = {
            'applicationId': newBusinessApplicationId,
            'perilsDeductibles': perilsDeductibles !== undefined ? perilsDeductibles : null,
            'risk': risk !== undefined ? risk : null,
            'disqualifyingConditionsAgreement': disqualifyingConditionsAgreement !== undefined ? disqualifyingConditionsAgreement : null,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get price indication.';
            }
            throw error;
        }
    }

    public async rateNewBusinessApplicationCommercial(newBusinessApplicationId: number,
        includeTriaCoverage?: boolean,
        perilsDeductibles?: PerilDeductible[],
        risks?: BuildingRequestCommercialExisting[],
        disqualifyingConditionsAgreement?: DisqualifyingConditionsAgreement): Promise<NewBusinessApplication> {

        let url: string = `${this.controllerUrl}/comm/price-indication`;
        let data = {
            'applicationId': newBusinessApplicationId,
            'includeTriaCoverage': includeTriaCoverage ?? null,
            'perilsDeductibles': perilsDeductibles !== undefined ? perilsDeductibles : null,
            'risks': risks !== undefined ? risks : null,
            'disqualifyingConditionsAgreement': disqualifyingConditionsAgreement !== undefined ? disqualifyingConditionsAgreement : null,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get price indication.';
            }
            throw error;
        }
    }

    public async getNewBusinessApplication(newBusinessApplicationId: number): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get application.';
            }
            throw error;
        }
    }

    public async deleteBuilding(buildingGuid: string): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/building/${buildingGuid}`;

        try {
            let response: Response = await super.makeDeleteCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not remove building.';
            }
            throw error;
        }
    }

    public async saveInsuredInfo(newBusinessApplicationId: number,
        insuredInfo: InsuredInfo): Promise<NewBusinessApplication> {

        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/insured`;
        let data = insuredInfo;

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save the insured information.';
            }
            throw error;
        }
    }

    public async addMortgagee(newBusinessApplicationId: number,
        mortgagee: MortgageeRequest): Promise<MortgageeRequestWithStatusAndId> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/mortgagee`;
        let data = mortgagee;

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save the mortgagee information.';
            }
            throw error;
        }
    }

    public async updateMortgagee(mortgageeGuid: string,
        mortgagee: MortgageeRequest): Promise<MortgageeRequestWithStatusAndId> {
        let url: string = `${this.controllerUrl}/mortgagee/${mortgageeGuid}`;
        let data = mortgagee;

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save the mortgagee information.';
            }
            throw error;
        }
    }

    public async deleteMortgagee(mortgageeGuid: string): Promise<void> {
        let url: string = `${this.controllerUrl}/mortgagee/${mortgageeGuid}`;

        try {
            await super.makeDeleteCall(url);
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not delete the mortgagee information.';
            }
            throw error;
        }
    }

    public async confirmNoMortgagees(newBusinessApplicationId: number): Promise<void> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/no-mortgagees`;

        try {
            await super.makePostCall(url, null);
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save the mortgagee status.';
            }
            throw error;
        }
    }

    public async validateMortgagees(newBusinessApplicationId: number): Promise<void> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/validate-mortgagees`;

        try {
            await super.makePostCall(url, null);
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not validate the mortgagees.';
            }
            throw error;
        }
    }

    public async saveInceptionDate(newBusinessApplicationId: number,
        inceptionDate: Date): Promise<void> {

        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/inception-date`;
        let data = {
            'requestedInceptionDate': inceptionDate,
        };

        try {
            await super.makePutCall(url, data);
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not save requested inception date.';
            }
            throw error;
        }
    }

    public async submitNewBusinessApplication(newBusinessApplicationId: number,
        acknowledgements: AcknowledgementText[],
        signature: Signature): Promise<NewBusinessApplication> {

        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/sign-and-submit-to-underwriting`;
        let data = {
            'acknowledgements': acknowledgements,
            'signature': signature,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not submit application.';
            }
            throw error;
        }
    }

    public async unsubmitNewBusinessApplication(newBusinessApplicationId: number): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/unsubmit`;

        try {
            let response: Response = await super.makePostCall(url, {});
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not unsubmit application.';
            }
            throw error;
        }
    }

    public async resubmitNewBusinessApplication(newBusinessApplicationId: number, note: string, document: DocumentUpload | undefined): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/resubmit`;
        let data = {
            'note': note,
            'document': document !== undefined
                ? {
                    'name': document.docName,
                    'data': [].slice.call(document.docData),
                }
                : null,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not resubmit application.';
            }
            throw error;
        }
    }

    public async getPossibleDisclosureTexts(newBusinessApplicationId: number): Promise<PossibleDisclosureTexts> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/disclosures`;

        try {
            let response: Response = await super.makeGetCall(url, {});
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get disclosure texts.';
            }
            throw error;
        }
    }

    public async getBindingAuthorityEligibilityResults(newBusinessApplicationId: number): Promise<BindingAuthorityEligibilityResult[]> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/eligibility/records`;

        try {
            let response: Response = await super.makeGetCall(url, {});
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get binding authority eligibilities .';
            }
            throw error;
        }
    }

    public async getNewBusinessApplicationDocument(newBusinessApplicationId: number): Promise<NewBusinessApplicationDocument> {
        let url: string = `application/document/${newBusinessApplicationId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get application document.';
            }
            throw error;
        }
    }

    public async setTargetWorkflowVersion(newBusinessApplicationId: number, newVersionNumber: number): Promise<void> {
        let url: string = `${this.controllerUrl}/${newBusinessApplicationId}/set-target-workflow-version/${newVersionNumber}`;

        try {
            await super.makePutCall(url, {});
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not update the targeted workflow version.';
            }
            throw error;
        }
    }

    public async getPossiblePerilsAndDeductibles(newBusinessApplicationId: number): Promise<PossiblePeril[]> {
        let url: string = `${this.controllerUrl}/possible-perils-deductibles/${newBusinessApplicationId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get possible perils and their deductibles for application.';
            }
            throw error;
        }
    }
};